import axios from "axios";
import { getToken } from "@/api/config/token";
import { getAPIbaseURL, getEntidade } from "@/api/config/init";
import Swal from "sweetalert2";
import store from "@/state/store";
import router from "@/router";
import appConfig from "@/dados/app.config.json";

const HEADERS = {
    ENTIDADE: "X-ACCESS-ENTIDADE",
    TOKEN: "X-ACCESS-TOKEN",
    API_VERSION: "x-api-version",
};

const TIMEOUT_MS = 15 * 60 * 1000; // 15 minutos

// Flag para evitar múltiplos alertas simultâneos
let isUpdateAlertOpen = false;
let isSessionExpiredAlertOpen = false;

const service = axios.create({
    baseURL: getAPIbaseURL(),
    timeout: TIMEOUT_MS,
});

// Request interceptor — injeta token e entidade em cada requisição
service.interceptors.request.use(
    (config) => {
        config.headers[HEADERS.ENTIDADE] = getEntidade();
        config.headers[HEADERS.TOKEN] = getToken();
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor
service.interceptors.response.use(
    (response) => {
        checkForUpdate(response.headers);
        return response.data;
    },
    (error) => {
        if (!error.response) {
            return Promise.reject(error);
        }

        const { data, status } = error.response;

        if (status === 401) {
            handleSessionExpired(data?.mensagem);
        }

        return Promise.reject(error);
    }
);

/**
 * Verifica se há uma nova versão da aplicação disponível.
 * Exibe um alerta apenas uma vez por sessão de verificação.
 */
function checkForUpdate(headers) {
    const versaoApi = headers[HEADERS.API_VERSION];
    const versaoFront = appConfig.versao;

    if (!versaoApi || versaoApi === versaoFront || isUpdateAlertOpen) {
        return;
    }

    isUpdateAlertOpen = true;

    Swal.fire({
        title: "Nova Atualização Disponível",
        html: "Para evitar inconsistência na base de dados é recomendado atualizar imediatamente. <br> Os dados digitados não salvos serão perdidos.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        confirmButtonText: "Atualizar agora",
        cancelButtonText: "Mais tarde",
    }).then((result) => {
        isUpdateAlertOpen = false;
        if (result.isConfirmed) {
            window.location.reload();
        }
    });
}

/**
 * Trata sessão expirada (401) — exibe alerta e faz logout.
 * Ignora se já estiver na tela de login ou se o alerta já estiver aberto.
 */
function handleSessionExpired(mensagem) {
    const rota = router.currentRoute;

    if (rota.name === "index" || isSessionExpiredAlertOpen) {
        return;
    }

    isSessionExpiredAlertOpen = true;

    Swal.fire({
        title: mensagem || "Sessão expirada",
        html: "Seu acesso expirou. <br> Faça o login novamente para continuar acessando.",
        icon: "warning",
        showCancelButton: false,
        confirmButtonColor: "#9a9d9c",
        confirmButtonText: "Fazer Login",
    }).then((result) => {
        isSessionExpiredAlertOpen = false;
        if (result.isConfirmed) {
            store.dispatch("auth/logOut").catch(console.error);
        }
    });
}

export default service;
